import { useEffect, useState, type RefObject } from "react";
import { isMobileOrTablet } from "../../../utils/media";

// lock()/unlock() are missing from some TS DOM libs and browsers
// (iOS Safari cannot lock orientation at all)
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: "landscape") => Promise<void>;
  unlock?: () => void;
};

/** Resolves true when the browser actually locked to landscape. */
const lockLandscape = async (): Promise<boolean> => {
  const orientation = screen.orientation as LockableOrientation | undefined;
  if (!orientation?.lock) return false;
  try {
    await orientation.lock("landscape");
    return true;
  } catch {
    return false;
  }
};

/** iPhone / iPad Safari (iPadOS reports "MacIntel" with touch points). */
const isIOS = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const unlockOrientation = () => {
  const orientation = screen.orientation as LockableOrientation | undefined;
  try {
    orientation?.unlock?.();
  } catch {
    /* not locked / not supported */
  }
};

type IOSVideo = HTMLVideoElement & {
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
};

/**
 * Fullscreen CSS for the player box matched by `selector`. Pair with the
 * `pseudo-fullscreen` / `rotated` classes from useMobileFullscreen.
 */
export const mobileFullscreenCss = (selector: string) => `
    /* separate rules — an unknown selector in a list drops the whole rule */
    ${selector}:fullscreen {
      width: 100%;
      height: 100% !important;
      max-height: none;
      aspect-ratio: auto;
    }
    ${selector}:-webkit-full-screen {
      width: 100%;
      height: 100% !important;
      max-height: none;
      aspect-ratio: auto;
    }
    /* iOS Safari has no element fullscreen on iPhone and no orientation
       lock, so "fullscreen" there is a fixed full-viewport box */
    ${selector}.pseudo-fullscreen {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100dvh !important;
      max-height: none;
      aspect-ratio: auto;
      z-index: 2147483000;
      background: black;
    }
    /* Phone held in portrait while fullscreen and the browser cannot lock
       landscape: turn the box 90deg so the video fills the screen */
    ${selector}.rotated {
      position: fixed;
      inset: auto;
      top: 50%;
      left: 50%;
      width: 100dvh !important;
      height: 100vw !important;
      max-height: none;
      aspect-ratio: auto;
      transform: translate(-50%, -50%) rotate(90deg);
      transform-origin: center center;
    }
`;

/**
 * Container fullscreen that also works on phones: locks landscape on
 * Android, and on iOS (no element fullscreen) falls back to a fixed
 * full-viewport box rotated 90deg while the phone is held in portrait.
 */
export function useMobileFullscreen(
  containerRef: RefObject<HTMLElement | null>,
  videoRef: RefObject<HTMLVideoElement | null>,
) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  // real fullscreen on a mobile browser that refused the landscape lock
  const [lockFailed, setLockFailed] = useState(false);
  const [isPortrait, setIsPortrait] = useState(
    () => window.matchMedia("(orientation: portrait)").matches,
  );

  useEffect(() => {
    const handler = () => {
      const video = videoRef.current as IOSVideo | null;
      const fullscreen =
        !!document.fullscreenElement || !!video?.webkitDisplayingFullscreen;
      setIsFullscreen(fullscreen);
      // also covers leaving fullscreen with the back button / gesture
      if (!fullscreen) {
        unlockOrientation();
        setLockFailed(false);
      }
    };

    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, [videoRef]);

  // stops the CSS rotation once the phone is physically turned landscape
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const onChange = () => setIsPortrait(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // no page scrolling behind the fixed pseudo-fullscreen box
  useEffect(() => {
    if (!pseudoFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [pseudoFullscreen]);

  const fullscreenActive = isFullscreen || pseudoFullscreen;
  const rotated =
    isPortrait && (pseudoFullscreen || (isFullscreen && lockFailed));

  const toggleFullscreen = async () => {
    const video = videoRef.current as IOSVideo | null;
    const container = containerRef.current;
    if (!video || !container) return;

    if (pseudoFullscreen) {
      setPseudoFullscreen(false);
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      // iOS Safari: native video fullscreen never rotates on its own and
      // drops the watermark, so use the CSS fullscreen (rotated) instead
      if (isIOS()) {
        setPseudoFullscreen(true);
        return;
      }

      // container fullscreen keeps the custom controls and overlays inside
      if (container.requestFullscreen) {
        await container.requestFullscreen();
        if (isMobileOrTablet()) setLockFailed(!(await lockLandscape()));
        return;
      }

      video.webkitEnterFullscreen?.();
    } catch (error) {
      console.error("Fullscreen error:", error);

      if (isMobileOrTablet()) {
        setPseudoFullscreen(true);
        return;
      }

      try {
        video.webkitEnterFullscreen?.();
      } catch (fallbackError) {
        console.error("iOS fullscreen fallback error:", fallbackError);
      }
    }
  };

  return { fullscreenActive, pseudoFullscreen, rotated, toggleFullscreen };
}
